import React, { useState, useCallback, useMemo } from 'react';

// Constants for better maintainability
const FORM_CONFIG = {
  account_holder_name: { label: 'Account Holder Name *', type: 'text', required: true, placeholder: 'Enter account holder name' },
  account_number: { label: 'Account Number *', type: 'text', required: true, placeholder: 'Enter account number' },
  ifsc_code: { label: 'IFSC Code *', type: 'text', required: true, placeholder: 'Enter IFSC code', transform: 'uppercase' },
  bank_name: { label: 'Bank Name', type: 'text', required: false, placeholder: 'Enter bank name' },
  branch_name: { label: 'Branch Name', type: 'text', required: false, placeholder: 'Enter branch name' }
};

const FILE_CONFIG = {
  allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  maxSize: 5 * 1024 * 1024, // 5MB
  accept: 'image/jpeg,image/png,image/gif,image/webp'
};

const BankAccountForm = ({ 
  bankAccount, 
  onSave, 
  onCancel, 
  loading = false 
}) => {
  const [formData, setFormData] = useState(() => ({
    account_holder_name: bankAccount?.account_holder_name || '',
    account_number: bankAccount?.account_number || '',
    ifsc_code: bankAccount?.ifsc_code || '',
    bank_name: bankAccount?.bank_name || '',
    branch_name: bankAccount?.branch_name || '',
  }));
  
  const [passbookImage, setPassbookImage] = useState(null);
  const [existingPassbookKey, setExistingPassbookKey] = useState(bankAccount?.passbook_image_key || '');

  // Memoized handlers
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    const fieldConfig = FORM_CONFIG[name];
    
    setFormData(prev => ({
      ...prev,
      [name]: fieldConfig?.transform === 'uppercase' ? value.toUpperCase() : value
    }));
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type and size
    if (!FILE_CONFIG.allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, WebP)');
      return;
    }
    
    if (file.size > FILE_CONFIG.maxSize) {
      alert('File size should be less than 5MB');
      return;
    }
    
    setPassbookImage(file);
  }, []);

  // Memoized validation functions
  const validateForm = useCallback(() => {
    // Check required fields
    const requiredFields = Object.entries(FORM_CONFIG)
      .filter(([_, config]) => config.required)
      .map(([field]) => field);

    const missingFields = requiredFields.filter(field => !formData[field].trim());
    
    if (missingFields.length > 0) {
      alert('Please fill in all required fields');
      return false;
    }

    // IFSC code validation
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(formData.ifsc_code)) {
      alert('Please enter a valid IFSC code');
      return false;
    }

    return true;
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Create FormData for file upload
    const submitData = new FormData();
    
    // Append form data
    Object.keys(formData).forEach(key => {
      submitData.append(key, formData[key]);
    });
    
    // Append file data
    if (passbookImage) {
      submitData.append('passbookImage', passbookImage);
    }
    
    if (existingPassbookKey) {
      submitData.append('existing_passbook_key', existingPassbookKey);
    }

    try {
      await onSave(submitData);
    } catch (error) {
      console.error('Error saving bank account:', error);
    }
  }, [formData, passbookImage, existingPassbookKey, validateForm, onSave]);

  // Memoized form fields
  const renderFormFields = useMemo(() => 
    Object.entries(FORM_CONFIG).map(([fieldName, config]) => (
      <div key={fieldName} className="form-group">
        <label htmlFor={fieldName}>{config.label}</label>
        <input
          type={config.type}
          id={fieldName}
          name={fieldName}
          value={formData[fieldName]}
          onChange={handleChange}
          required={config.required}
          placeholder={config.placeholder}
          style={config.transform ? { textTransform: config.transform } : {}}
        />
      </div>
    )), [formData, handleChange]
  );

  // Memoized file input
  const renderFileInput = useMemo(() => (
    <div className="form-group">
      <label htmlFor="passbookImage">Passbook Image</label>
      <input
        type="file"
        id="passbookImage"
        accept={FILE_CONFIG.accept}
        onChange={handleFileChange}
      />
      <small>Upload a clear image of your bank passbook (Max 5MB)</small>
    </div>
  ), [handleFileChange]);

  // Memoized form actions
  const renderFormActions = useMemo(() => (
    <div className="form-actions">
      <button 
        type="button" 
        className="btn-cancel" 
        onClick={onCancel}
        disabled={loading}
      >
        Cancel
      </button>
      <button 
        type="submit" 
        className="btn-save"
        disabled={loading}
      >
        {loading ? 'Saving...' : (bankAccount ? 'Update' : 'Save')}
      </button>
    </div>
  ), [onCancel, loading, bankAccount]);

  return (
    <div className="bank-account-form-overlay">
      <div className="bank-account-form-modal">
        <div className="form-header">
          <h2>{bankAccount ? 'Update Bank Account' : 'Add Bank Account'}</h2>
          <button className="btn-close" onClick={onCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="bank-account-form">
          {renderFormFields}
          {renderFileInput}
          {renderFormActions}
        </form>
      </div>
    </div>
  );
};

export default React.memo(BankAccountForm);